import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { media } from '@/utils';

import { HelpAiChat } from './HelpAiChat';
import { useHelpAiChat } from './HelpAiChatContext';

/**
 * Asystent AI panelu „Stefan" dokowany przy prawej krawędzi ekranu (wzór
 * ElevenLabs). Otwiera go guzik „Zapytaj" w TopBarze (globalny stan w
 * `HelpAiChatContext`), a Layout jednocześnie wpycha treść w lewo i zwija
 * sidebar, więc na desktopie panel NIE przykrywa treści — dokuje się obok.
 *
 * Na tablecie/telefonie brakuje miejsca na wcięcie, więc panel zachowuje się
 * jak klasyczna szuflada z przyciemnionym tłem (klik w tło zamyka).
 *
 * Reużywa istniejący `HelpAiChat` — tylko opakowanie. Backend bez zmian.
 */

export const DOCK_WIDTH = 400;

/* Przyciemnienie treści — tylko na tabletDown (overlay mode). Na desktopie
   treść jest wpychana w lewo, więc backdrop jest zbędny (i przeszkadzałby). */
const Overlay = styled.div<{ $open: boolean }>`
  display: none;

  ${media.tabletDown} {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(15, 23, 42, 0.32);
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    transition: opacity 0.28s ease, visibility 0.28s ease;
  }
`;

/* Dokowany panel przy prawej krawędzi, pełna wysokość. Wysuwa się z prawej
   (translateX 100% → 0). */
const Dock = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  width: ${DOCK_WIDTH}px;
  max-width: 100vw;
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1), box-shadow 0.32s ease;
  box-shadow: ${({ $open }) => ($open ? '-16px 0 48px rgba(0, 0, 0, 0.12)' : 'none')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  border-left: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#e8eaee')};

  ${media.tabletDown} {
    box-shadow: ${({ $open }) => ($open ? '-16px 0 48px rgba(0, 0, 0, 0.18)' : 'none')};
  }

  @media (max-width: 480px) {
    width: 100vw;
  }
`;

/* Kolumna wewnątrz doku: opcjonalny banner „agent chce się nauczyć" na górze
   (renderowany wewnątrz HelpAiChat) + czat wypełniający resztę wysokości. */
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};

  /* HelpAiChat ma własną Card (height: 460px, align-self: start, border-radius:
     12px) — w trybie doku wypełnia całą szerokość i resztę wysokości oraz ma
     ścięte zaokrąglenia przy krawędzi. */
  & > div:last-child {
    flex: 1;
    min-height: 0;
    height: auto;
    width: 100%;
    align-self: stretch;
    border-radius: 0;
    border: none;
  }
`;

export const DockedHelpAiChat: FC = () => {
  const { t } = useTranslation();
  const { isOpen, hasOpened, close } = useHelpAiChat();

  // Blokada przewijania strony gdy panel otwarty — na desktopie treść i tak ma
  // `overflow: hidden` (Main), ale na tablecie/telefonie przewija się `body`,
  // więc chowamy jego scroll i zwracamy poprzednią wartość przy zamknięciu.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  return (
    <>
      <Overlay $open={isOpen} onClick={close} aria-hidden="true" />

      <Dock
        $open={isOpen}
        role="dialog"
        aria-modal="false"
        aria-hidden={!isOpen}
        aria-label={t('helpAiChat.title', 'Asystent AI')}
      >
        {hasOpened && (
          <Panel>
            {/* Banner „agent chce się czegoś nauczyć" renderuje się wewnątrz
                HelpAiChat, pod headerem Stefana. Guzik X zamykający panel jest
                teraz w headerze Stefana (onClose). */}
            <HelpAiChat onClose={close} />
          </Panel>
        )}
      </Dock>
    </>
  );
};
